import * as THREE from 'three';

/**
 * Three.js assigns `compileAsync` on each WebGLRenderer *instance* (constructor body),
 * not on the prototype. The stock poller does:
 *   `properties.get(material).currentProgram.isReady()`
 * with no null check. During material swaps, `currentProgram` may be undefined, or the
 * GL program may already be deleted — `isReady()` then hits
 * `glGetProgramiv: Program object expected` (browser WebGL log, not a JS throw).
 *
 * Critical: WebGL does NOT throw on invalid program handles — it only logs. try/catch
 * around getProgramParameter never silences the console. We must never call the native
 * getProgramParameter for deleted handles.
 *
 * We cannot replace `THREE.WebGLRenderer` under Turbopack/ESM (getter-only export).
 * Patch each renderer instance + use `compileAsyncSafe` at call sites.
 */

const PATCHED = '__c3dCompileAsyncPatched';
const GL_PARAM_PATCHED = '__c3dGetProgramParameterPatched';
const GL_PROTO_PATCHED = '__c3dGetProgramParameterProtoPatched';

/** KHR_parallel_shader_compile completion enum */
const COMPLETION_STATUS_KHR = 0x91b1;

type WebGLProgramLike = {
  isReady?: () => boolean;
  /** GL program handle; set to undefined in Three's `WebGLProgram.destroy()`. */
  program?: WebGLProgram | null;
};

type GlExtras = {
  [GL_PARAM_PATCHED]?: boolean;
  [GL_PROTO_PATCHED]?: boolean;
  __c3dDeadPrograms?: WeakSet<object>;
  __c3dLivePrograms?: WeakSet<object>;
  __c3dTrackAllPrograms?: boolean;
};

/**
 * Patch WebGL*RenderingContext prototypes once so every context never hits native
 * getProgramiv on a *deleted* program handle. Only the dead-set is blocked —
 * unknown/live handles still go to the driver (safe for programs created before patch).
 */
function patchGetProgramParameterPrototype(): void {
  if (typeof WebGLRenderingContext === 'undefined') return;
  const protos: object[] = [];
  try {
    if (WebGLRenderingContext?.prototype) protos.push(WebGLRenderingContext.prototype);
  } catch {
    /* ignore */
  }
  try {
    if (typeof WebGL2RenderingContext !== 'undefined' && WebGL2RenderingContext?.prototype) {
      protos.push(WebGL2RenderingContext.prototype);
    }
  } catch {
    /* ignore */
  }

  // One shared dead-set for all contexts — deleteProgram marks handles here.
  const deadPrograms = new WeakSet<object>();

  for (const proto of protos) {
    const p = proto as GlExtras & {
      getProgramParameter: (
        this: WebGLRenderingContext,
        program: WebGLProgram,
        pname: number,
      ) => GLint | GLboolean;
      deleteProgram: (this: WebGLRenderingContext, program: WebGLProgram | null) => void;
    };
    if (!p || p[GL_PROTO_PATCHED] || typeof p.getProgramParameter !== 'function') continue;
    p[GL_PROTO_PATCHED] = true;

    const origGet = p.getProgramParameter;
    const origDelete = typeof p.deleteProgram === 'function' ? p.deleteProgram : null;

    if (origDelete) {
      p.deleteProgram = function patchedProtoDeleteProgram(
        this: WebGLRenderingContext,
        program: WebGLProgram | null,
      ) {
        if (program && typeof program === 'object') deadPrograms.add(program);
        return origDelete.call(this, program);
      };
    }

    const safeParam = (gl: WebGLRenderingContext, pname: number): boolean | number => {
      if (pname === COMPLETION_STATUS_KHR || pname === gl.LINK_STATUS) return true;
      if (pname === gl.VALIDATE_STATUS) return true;
      if (pname === gl.DELETE_STATUS) return true;
      if (pname === gl.ACTIVE_UNIFORMS || pname === gl.ACTIVE_ATTRIBUTES) return 0;
      return 0;
    };

    p.getProgramParameter = function patchedProtoGetProgramParameter(
      this: WebGLRenderingContext,
      program: WebGLProgram,
      pname: number,
    ) {
      if (program == null) return safeParam(this, pname) as ReturnType<typeof origGet>;
      if (typeof program === 'object' && deadPrograms.has(program)) {
        return safeParam(this, pname) as ReturnType<typeof origGet>;
      }
      try {
        return origGet.call(this, program, pname);
      } catch {
        return safeParam(this, pname) as ReturnType<typeof origGet>;
      }
    };
  }
}

try {
  patchGetProgramParameterPrototype();
} catch {
  /* ignore */
}

/**
 * Intercept create/delete/getProgramParameter so deleted handles never hit the native
 * getProgramiv (which only logs GL_INVALID_VALUE — it does not throw).
 */
function patchGetProgramParameter(
  gl: WebGLRenderingContext | WebGL2RenderingContext | undefined,
): void {
  if (!gl || (gl as GlExtras)[GL_PARAM_PATCHED]) return;
  if (typeof gl.getProgramParameter !== 'function') return;

  const orig = gl.getProgramParameter.bind(gl);
  const origDelete =
    typeof gl.deleteProgram === 'function' ? gl.deleteProgram.bind(gl) : null;
  const origCreate =
    typeof gl.createProgram === 'function' ? gl.createProgram.bind(gl) : null;

  const deadPrograms = new WeakSet<object>();
  const livePrograms = new WeakSet<object>();
  const extras = gl as GlExtras;
  extras[GL_PARAM_PATCHED] = true;
  extras.__c3dDeadPrograms = deadPrograms;
  extras.__c3dLivePrograms = livePrograms;
  // Until we've seen at least one createProgram through the patch, allow unknown
  // handles (programs linked before the patch). After that, only live set is safe.
  extras.__c3dTrackAllPrograms = false;

  if (origCreate) {
    gl.createProgram = function patchedCreateProgram() {
      const program = origCreate();
      if (program && typeof program === 'object') {
        livePrograms.add(program);
        extras.__c3dTrackAllPrograms = true;
      }
      return program;
    } as typeof gl.createProgram;
  }

  if (origDelete) {
    gl.deleteProgram = function patchedDeleteProgram(program: WebGLProgram | null) {
      if (program && typeof program === 'object') {
        deadPrograms.add(program);
        livePrograms.delete(program);
      }
      return origDelete(program as WebGLProgram);
    } as typeof gl.deleteProgram;
  }

  const safeParam = (pname: number): boolean | number => {
    if (pname === COMPLETION_STATUS_KHR || pname === gl.LINK_STATUS) return true;
    if (pname === gl.VALIDATE_STATUS) return true;
    if (pname === gl.DELETE_STATUS) return true;
    if (pname === gl.ACTIVE_UNIFORMS || pname === gl.ACTIVE_ATTRIBUTES) return 0;
    return 0;
  };

  const shouldBlock = (program: WebGLProgram | null | undefined): boolean => {
    if (program == null) return true;
    if (typeof program === 'object') {
      if (deadPrograms.has(program)) return true;
      // After we own createProgram, refuse unknowns — prevents native GL_INVALID_VALUE logs.
      if (extras.__c3dTrackAllPrograms && !livePrograms.has(program)) return true;
    }
    return false;
  };

  const patchedGet = function patchedGetProgramParameter(
    program: WebGLProgram,
    pname: number,
  ) {
    if (shouldBlock(program)) return safeParam(pname);
    return orig(program, pname);
  };

  try {
    Object.defineProperty(gl, 'getProgramParameter', {
      configurable: true,
      enumerable: true,
      writable: true,
      value: patchedGet,
    });
  } catch {
    gl.getProgramParameter = patchedGet as typeof gl.getProgramParameter;
  }
}

function isGlProgramAlive(
  gl: WebGLRenderingContext | WebGL2RenderingContext | undefined,
  program: WebGLProgramLike,
): boolean {
  const handle = program.program;
  if (handle == null) return false;
  const extras = gl as GlExtras | undefined;
  if (extras?.__c3dDeadPrograms && typeof handle === 'object' && extras.__c3dDeadPrograms.has(handle)) {
    return false;
  }
  if (extras?.__c3dTrackAllPrograms && extras.__c3dLivePrograms && typeof handle === 'object') {
    return extras.__c3dLivePrograms.has(handle);
  }
  if (!gl || typeof gl.isProgram !== 'function') return true;
  try {
    return gl.isProgram(handle);
  } catch {
    return false;
  }
}

/**
 * Query completion without going through Three's isReady() closure (which can
 * still hold a deleted GL handle after destroy()).
 */
function isProgramComplete(
  gl: WebGLRenderingContext | WebGL2RenderingContext | undefined,
  program: WebGLProgramLike,
): boolean {
  const handle = program.program;
  if (handle == null || !gl) return true;
  if (!isGlProgramAlive(gl, program)) return true;
  try {
    if (!gl.getExtension?.('KHR_parallel_shader_compile')) return true;
    return !!gl.getProgramParameter(handle, COMPLETION_STATUS_KHR);
  } catch {
    return true;
  }
}

/** Safe parallel compile — use this instead of `gl.compileAsync` at call sites. */
export function compileAsyncSafe(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Object3D,
  camera: THREE.Camera,
  targetScene: THREE.Object3D | null = null,
): Promise<THREE.Object3D> {
  const gl = (() => {
    try {
      return renderer.getContext?.();
    } catch {
      return undefined;
    }
  })();
  patchGetProgramParameter(gl);

  let materials: Set<THREE.Material>;
  try {
    materials = (renderer as any).compile(scene, camera, targetScene) as Set<THREE.Material>;
  } catch {
    return Promise.resolve(scene);
  }

  const properties = (renderer as any).properties as {
    get?: (obj: unknown) => { currentProgram?: WebGLProgramLike | undefined } | undefined;
  } | undefined;

  // Defuse Three's isReady() closure on programs we just compiled — stock compileAsync
  // (or a stale HMR poller) can still call it after destroy().
  if (properties && typeof properties.get === 'function' && materials instanceof Set) {
    for (const material of materials) {
      try {
        const program = properties.get(material)?.currentProgram as
          | (WebGLProgramLike & { __c3dIsReadyPatched?: boolean })
          | undefined;
        if (!program || program.__c3dIsReadyPatched) continue;
        if (typeof program.isReady !== 'function') continue;
        program.__c3dIsReadyPatched = true;
        program.isReady = () => {
          if (!isGlProgramAlive(gl, program)) return true;
          return isProgramComplete(gl, program);
        };
      } catch {
        /* ignore */
      }
    }
  }

  return new Promise<THREE.Object3D>((resolve) => {
    if (!properties || typeof properties.get !== 'function' || !(materials instanceof Set)) {
      resolve(scene);
      return;
    }

    // Cancel older in-flight polls on this renderer when a newer compile starts.
    const r = renderer as any;
    const gen = ((r.__c3dCompileGen as number) || 0) + 1;
    r.__c3dCompileGen = gen;

    let checks = 0;
    const maxChecks = 200; // ~2s at 10ms

    const check = () => {
      if (r.__c3dCompileGen !== gen) {
        resolve(scene);
        return;
      }
      checks += 1;
      try {
        for (const material of [...materials]) {
          let program: WebGLProgramLike | undefined;
          try {
            program = properties.get!(material)?.currentProgram;
          } catch {
            materials.delete(material);
            continue;
          }
          if (!program) {
            materials.delete(material);
            continue;
          }
          // Never call Three's isReady() — its closure can outlive this.program.
          if (!isGlProgramAlive(gl, program) || isProgramComplete(gl, program)) {
            materials.delete(material);
          }
        }
      } catch {
        materials.clear();
      }

      if (materials.size === 0 || checks >= maxChecks) {
        resolve(scene);
        return;
      }
      setTimeout(check, 10);
    };

    try {
      check();
    } catch {
      resolve(scene);
    }
  });
}

/** Replace an existing renderer's instance `compileAsync` (R3F `gl`, thumbnails, etc.). */
export function patchCompileAsyncOnRenderer(renderer: THREE.WebGLRenderer | null | undefined): void {
  if (!renderer) return;
  const r = renderer as any;
  if (r[PATCHED]) {
    // Still ensure GL guard is on (context restore / late getContext).
    try {
      patchGetProgramParameter(renderer.getContext?.());
    } catch {
      /* ignore */
    }
    // Always silence per-program info-log stalls + invalid-program spam in the viewer.
    try {
      if (renderer.debug) renderer.debug.checkShaderErrors = false;
    } catch {
      /* ignore */
    }
    return;
  }
  if (typeof r.compile !== 'function') return;

  r[PATCHED] = true;
  try {
    patchGetProgramParameter(renderer.getContext?.());
  } catch {
    /* ignore */
  }
  try {
    if (renderer.debug) renderer.debug.checkShaderErrors = false;
  } catch {
    /* ignore */
  }

  r.compileAsync = function (
    scene: THREE.Object3D,
    camera: THREE.Camera,
    targetScene: THREE.Object3D | null = null,
  ) {
    return compileAsyncSafe(this, scene, camera, targetScene);
  };
}

/**
 * No-op constructor wrap — ESM/`three` exports are getter-only under Turbopack.
 * Kept so existing `patchCompileAsyncSafe()` call sites stay valid.
 */
export function patchCompileAsyncSafe(): void {
  // Intentionally empty: cannot assign THREE.WebGLRenderer in this bundler.
}
